/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Misc imports
import { NODE_TYPES } from '../../constants/flowGraph';
import { CHAPTERSOURCE_SCHEMAS } from '../../constants/main';
import { generateIntroNodeId } from './automatedAnswer';
import { v4 as uuidv4 } from 'uuid';

/**
 * Transform a bots activity to only clone content to the target bots available languages
 * @param {Object} bots All bots, required to find the source/target bot
 * @param {String} tenantId The ID of the source bot
 * @param {Object} values Contains the target bot ID and the new name of the activity
 * @param {Object} activityToClone The existing activity to be cloned
 * @param {Boolean} isSingleActivity True if only one activity is to be cloned, false if multiple
 */
export const cloneFlowToTargetLanguage = (bots, tenantId, values, activityToClone, isSingleActivity) => {
  const sourceTenant = bots.items.find((bot) => bot.id === tenantId);
  const targetTenant = bots.items.find((bot) => bot.id === values.tenantId);

  const sourceTenantPrimaryLanguage = sourceTenant.settings.languages.primary;
  const targetTenantPrimaryLanguage = targetTenant.settings.languages.primary;

  const sourceTenantAdditionalLanguages = sourceTenant.settings.languages.additional;
  const targetTenantAdditionalLanguages = targetTenant.settings.languages.additional;

  const targetTenantLanguages = [...targetTenantAdditionalLanguages, targetTenantPrimaryLanguage];
  const sourceTenantLanguages = [...sourceTenantAdditionalLanguages, sourceTenantPrimaryLanguage];

  const oldIntroNodeId = activityToClone.content.nodes.find(node => node.type === NODE_TYPES.INTRO).id;

  let newActivity = { ...activityToClone };
  newActivity.id = uuidv4();
  if (isSingleActivity) {
    newActivity.name = {
      [targetTenantPrimaryLanguage]: `${values.newName}`
    };
  } else {
    newActivity.name = {
      [targetTenantPrimaryLanguage]: `${activityToClone.name[sourceTenantPrimaryLanguage]} (Copy)`
    };
  }

  newActivity.content.nodes.forEach((node) => {
    const oldNodeId = node.id;
    const newNodeId = node.type === NODE_TYPES.INTRO ? generateIntroNodeId(newActivity.id, newActivity.name[targetTenantPrimaryLanguage]) : uuidv4();

    newActivity.content = {
      ...activityToClone.content,
      schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH,
      // Update node IDs
      nodes: newActivity.content.nodes.map(node => {
        if (!node) {
          return null;
        }
        if (node.id === oldNodeId) {
          return { ...node, id: newNodeId };
        // Make sure jump nodes don't point to the parent activity, but to the new one
        } else if ((node.type === NODE_TYPES.JUMP || node.type === NODE_TYPES.USER_FEEDBACK) && node.jumpTo === oldIntroNodeId) {
          return { ...node, label: { [targetTenantPrimaryLanguage]: '' }, jumpTo: newNodeId };
        } else {
          // Initialize all the multilingual fields that possibly exist in a node
          // Different node types may have different multilingual properties
          let label = {};
          let validationFallbackMessage = {};
          let fileUploadFallbackMessage = {};
          let customUserInfoData = null;
          let multiOptions = {};
          let switchQuery = {};
          let submitLabel = {};
          let placeholder = {};
          for (const targetLang in targetTenantLanguages) {
            const lang = targetTenantLanguages[targetLang];

            // Only add label if content exists for this language
            if (sourceTenantLanguages.includes(lang) && node.label && node.label[lang]) {
              label[lang] = node.label[lang];
            }

            // For each node, find if the source node has content in the target language
            // If it does, copy its content, otherwise ignore it
            switch(node.type) {
              case NODE_TYPES.MULTI_OPTION:
                if (sourceTenantLanguages.includes(lang) && node.multiOptions && node.multiOptions[lang]) {
                  multiOptions[lang] = node.multiOptions[lang];
                }
                break;
              case NODE_TYPES.TEXT_INPUT:
              case NODE_TYPES.INPUT_QUERY:
                // Both nodes have validationFallbackMessage
                if (sourceTenantLanguages.includes(lang) && node.validationFallbackMessage && node.validationFallbackMessage[lang]) {
                  validationFallbackMessage[lang] = node.validationFallbackMessage[lang];
                }

                // Only for INPUT_QUERY
                if (node.type === NODE_TYPES.INPUT_QUERY && sourceTenantLanguages.includes(lang) && node.placeholder && node.placeholder[lang]) {
                  placeholder[lang] = node.placeholder[lang];
                }

                break;
              case NODE_TYPES.FILE_UPLOAD:
                if (sourceTenantLanguages.includes(lang) && node.fileUploadFallbackMessage && node.fileUploadFallbackMessage[lang]) {
                  fileUploadFallbackMessage[lang] = node.fileUploadFallbackMessage[lang];
                }
                break;
              case NODE_TYPES.USER_INFO:
                if (sourceTenantLanguages.includes(lang)) {
                  if (node.validationFallbackMessage && node.validationFallbackMessage[lang]) {
                    validationFallbackMessage[lang] = node.validationFallbackMessage[lang];
                  }
                  if (node.submitLabel && node.submitLabel[lang]) {
                    submitLabel[lang] = node.submitLabel[lang];
                  }

                  if (node.customUserInfoData) {
                    customUserInfoData = node.customUserInfoData?.map((customData) => {
                      const hasQuestion = customData.question && customData.question[lang];
                      const hasPlaceholder = customData.placeholder && customData.placeholder[lang];

                      if (hasQuestion || hasPlaceholder) {
                        let result = {};
                        if (hasQuestion) {
                          result.question = { [lang]: customData.question[lang] };
                        }
                        if (hasPlaceholder) {
                          result.placeholder = { [lang]: customData.placeholder[lang] };
                        }
                        return result;
                      }
                      return null;
                    }).filter((customData) => customData !== null);
                  }
                }
                break;
              case NODE_TYPES.SWITCH_QUERY:
                if (sourceTenantLanguages.includes(lang) && node.switchQuery && node.switchQuery[lang]) {
                  switchQuery[lang] = node.switchQuery[lang];
                }
                break;
              default:
                break;
            }
          }

          return {
            ...node,
            ...(Object.keys(label).length > 0 && { label: label }),
            ...(Object.keys(validationFallbackMessage).length > 0 && { validationFallbackMessage: validationFallbackMessage }),
            ...(Object.keys(fileUploadFallbackMessage).length > 0 && { fileUploadFallbackMessage: fileUploadFallbackMessage }),
            ...(Object.keys(multiOptions).length > 0 && { multiOptions: multiOptions }),
            ...(Object.keys(switchQuery).length > 0 && { switchQuery: switchQuery }),
            ...(Object.keys(submitLabel).length > 0 && { submitLabel: submitLabel }),
            ...(Array.isArray(customUserInfoData) && { customUserInfoData: customUserInfoData }),
            ...(Object.keys(placeholder).length > 0 && { placeholder: placeholder })
          };
        }
      }),
      // Update edge sources and targets
      edges: newActivity.content?.edges?.map(edge => ({
        ...edge,
        ...(edge.source === oldNodeId && { source: newNodeId }),
        ...(edge.target === oldNodeId && { target: newNodeId }),
        ...(edge.sourcePositionId?.includes(oldNodeId) && { sourcePositionId: edge.sourcePositionId.replace(oldNodeId, newNodeId) })
      }))
    };
  });

  return newActivity;
};
